import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiRefreshCw } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { usePullToRefresh } from '../hooks/usePullToRefresh';

/**
 * Pull-to-refresh wrapper component
 * Wraps content and provides pull-to-refresh functionality with visual indicator
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to wrap
 * @param {Function} props.onRefresh - Async function to call when refresh is triggered
 * @param {boolean} props.disabled - Disable pull-to-refresh
 * @param {string} props.className - Additional CSS classes for the container
 */
function PullToRefresh({
  children,
  onRefresh,
  disabled = false,
  className = '',
}) {
  const {
    isPulling,
    isRefreshing,
    pullDistance,
    progress,
    canRefresh,
    handlers,
  } = usePullToRefresh({
    onRefresh,
    disabled,
    threshold: 70,
    maxPull: 100,
  });

  // Show indicator when pulling or refreshing
  const showIndicator = isPulling || isRefreshing;

  return (
    <div
      className={`relative ${className}`}
      {...handlers}
      style={{ touchAction: isPulling ? 'none' : 'auto' }}
    >
      {/* Pull indicator */}
      <AnimatePresence>
        {showIndicator && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute left-0 right-0 flex justify-center pointer-events-none z-10"
            style={{
              top: 0,
              transform: `translateY(${Math.max(pullDistance - 50, 0)}px)`,
            }}
          >
            <motion.div
              className={`
                w-10 h-10 rounded-full flex items-center justify-center
                ${canRefresh || isRefreshing
                  ? 'bg-accent text-white'
                  : 'bg-card border border-border-subtle text-text-secondary'
                }
                shadow-lg
              `}
              animate={{
                scale: isRefreshing ? 1 : 0.8 + progress * 0.2,
                rotate: isRefreshing ? 360 : progress * 180,
              }}
              transition={{
                rotate: isRefreshing
                  ? { duration: 1, repeat: Infinity, ease: 'linear' }
                  : { duration: 0.1 },
                scale: { duration: 0.1 },
              }}
            >
              <SafeIcon
                icon={FiRefreshCw}
                className="w-5 h-5"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content with transform when pulling */}
      <motion.div
        animate={{
          y: isPulling || isRefreshing ? pullDistance : 0,
        }}
        transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30,
          duration: isPulling ? 0 : 0.3,
        }}
      >
        {children}
      </motion.div>
    </div>
  );
}

export default PullToRefresh;
